import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Phone } from "lucide-react";

const DISMISS_KEY = "fawks_sticky_lead_dismissed";
// Hide on pages that already have a strong primary CTA above-the-fold
const HIDE_ON = ["/book-demo", "/login"];

export const StickyLeadBar = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? window.scrollY / docH : 0;
      setVisible(pct > 0.5);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const hide =
    dismissed || HIDE_ON.some((p) => pathname === p) || !visible;

  return (
    <AnimatePresence>
      {!hide && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          data-testid="sticky-lead-bar"
          className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-3xl items-center justify-between gap-3 rounded-full border border-white/[0.08] bg-[rgba(5,8,22,0.92)] py-1.5 pl-5 pr-1.5 shadow-[0_20px_60px_-15px_rgba(125,104,194,0.4),0_0_0_1px_rgba(167,139,250,0.15)] backdrop-blur-xl sm:bottom-5"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#7D68C2] to-[#9B8AE8] sm:flex">
              <Phone className="h-3.5 w-3.5 text-white" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-white">
                Got 30 seconds?
              </div>
              <div className="truncate text-[11px] text-white/60">
                Hear a real Fawks AI call live · no signup
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Link
              to="/book-demo"
              data-testid="sticky-lead-cta"
              className="group inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-[#7D68C2] to-[#9B8AE8] px-4 py-2 text-xs font-semibold text-white sm:px-5 sm:text-sm"
            >
              See a demo
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button
              onClick={dismiss}
              data-testid="sticky-lead-dismiss"
              aria-label="Dismiss"
              className="ml-0.5 flex h-9 w-9 items-center justify-center rounded-full text-white/65 hover:bg-white/[0.06] hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyLeadBar;
